import { useMemo } from 'react';
import { useCompany } from '@/hooks/useCompany';
import {
  formatterCurrencyTruncated,
  formatterFactor,
  formatterPercentage,
} from '@/utilities/format';
import useGlossaryTerms from '@/hooks/useGlossaryTerms';

type KeyMetric = {
  title: string;
  value: string;
  tooltip?: string | null;
};

type KeyMetrics = [KeyMetric] | [KeyMetric, KeyMetric];

export const useNonBankMetrics = (canonicalURL: string) => {
  const { data } = useCompany(canonicalURL);
  const reportingCurrencyISO =
    data?.data.analysis?.data.extended.data.raw_data.data.currency_info
      .reporting_currency_iso || 'USD';
  const {
    cash_st_investments: totalCash,
    total_assets: totalAssets,
    total_current_liab: currentLiabilities,
    debt_to_equity_ratio: debtToEquityRatio,
    total_debt: totalDebt,
    net_interest_cover: netInterestCover,
    total_equity: totalEquity,
    long_term_liab: longTermLiabilities,
  } = data?.data.analysis?.data.extended.data.analysis.health || {};

  const term = useGlossaryTerms();

  const keyMetrics: KeyMetrics = useMemo(
    () => [
      {
        title: 'Debt to equity ratio',
        value: formatterPercentage(debtToEquityRatio),
        tooltip: term('debt to equity ratio'),
      },
      {
        title: 'Debt',
        value:
          typeof totalDebt === 'number'
            ? formatterCurrencyTruncated(
                totalDebt * 1_000_000,
                reportingCurrencyISO,
                2
              )
            : 'n/a',
        tooltip: term('total debt'),
      },
    ],
    [debtToEquityRatio, totalDebt, reportingCurrencyISO, term]
  );

  const metrics = useMemo(
    () =>
      [
        {
          title: 'Interest coverage ratio',
          value: formatterFactor(netInterestCover),
          tooltip: term('interest coverage ratio'),
        },
        {
          title: 'Cash',
          value:
            typeof totalCash === 'number'
              ? formatterCurrencyTruncated(
                  totalCash * 1_000_000,
                  reportingCurrencyISO,
                  2
                )
              : 'n/a',
          tooltip: term('cash and short term investments'),
        },
        {
          title: 'Equity',
          value:
            typeof totalEquity === 'number'
              ? formatterCurrencyTruncated(
                  totalEquity * 1_000_000,
                  reportingCurrencyISO,
                  2
                )
              : 'n/a',
          tooltip: term('total equity'),
        },
        {
          title: 'Total liabilities',
          value:
            typeof longTermLiabilities === 'number' &&
            typeof currentLiabilities === 'number'
              ? formatterCurrencyTruncated(
                  (longTermLiabilities + currentLiabilities) * 1_000_000,
                  reportingCurrencyISO,
                  2
                )
              : 'n/a',
          tooltip: term('total liabilities'),
        },
        {
          title: 'Total assets',
          value:
            typeof totalAssets === 'number'
              ? formatterCurrencyTruncated(
                  totalAssets * 1_000_000,
                  reportingCurrencyISO,
                  2
                )
              : 'n/a',
          tooltip: term('total assets'),
        },
      ] as const,
    [
      netInterestCover,
      totalCash,
      totalEquity,
      longTermLiabilities,
      reportingCurrencyISO,
      currentLiabilities,
      totalAssets,
      term,
    ]
  );

  return { keyMetrics, metrics };
};
